import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import { RootState } from '../app/ReduxStore.ts'
import {
  AcceptInviteAppRequest,
  AssignMemberRoleForMeetingAdminAppRequest,
  AssignRoleRequestWithUrlParameters,
  BatchAssignRolesAppRequest,
  BatchCancelRolesAppRequest,
  CancelAttendanceRequestWithUrlParameters,
  CancelMemberRoleForMeetingAdminAppRequest,
  ClubAdminInviteAppRequest,
  CreateAddressAppRequest,
  CreateClubMemberAppRequest,
  CreateGuestUserAppRequest,
  CreateScheduleAppRequest,
  DeleteAddressAppRequest,
  DeleteClubMemberAppRequest,
  DeleteScheduleAppRequest,
  ImportPreviewResponse,
  MeetingDetailsRequest,
  PatchUserAppRequest,
  UpdateAddressAppRequest,
  UpdateClubMemberAppRequest,
  UpdateScheduleAppRequest,
} from '../types/Types.ts'
import {
  Achievements,
  AdminUpdateSpeechRequest,
  Address,
  AssignRoleResponse,
  BatchAddMembersRequest,
  BatchAddMembersResponse,
  BatchRolesResponse,
  BillingRedirectDto,
  BillingStatusDto,
  CancelAttendanceResponse,
  Club,
  ClubDetails,
  ClubMember,
  CreateAddressRequest,
  CreateClubRequest,
  CreateClubResponse,
  DigestConfig,
  DigestConfigUpsertRequest,
  DigestSendNowResult,
  EmailTemplateResponse,
  EmailTemplateUpsertRequest,
  MeetingDetails,
  Pathway,
  RecurringMeeting,
  SignInLinkRequest,
  SignUpLinkRequest,
  UpdateAddressRequest,
  UpdateMeetingAgendaRequest,
  UpdateSpeechRequest,
  User,
} from '../generated'

// Address POST/PUT are multipart/form-data: the address payload is an
// application/json part named `address`, with an optional `file` part for the
// cover image. The JSON must be wrapped in a Blob so the part carries an
// application/json Content-Type — a plain string would be sent as text/plain.
const buildAddressFormData = (
  body: CreateAddressRequest | UpdateAddressRequest,
  file?: File
): FormData => {
  const formData = new FormData()
  formData.append(
    'address',
    new Blob([JSON.stringify(body)], { type: 'application/json' })
  )
  if (file) {
    formData.append('file', file)
  }
  return formData
}

export const speechAgendaApi = createApi({
  reducerPath: 'speechAgendaApi',
  baseQuery: fetchBaseQuery({
    baseUrl: `${import.meta.env.VITE_WEB_API_BASE_URL}`,
    prepareHeaders: (headers, { getState }) => {
      const token = (getState() as RootState).firebaseAuth.user?.accessToken
      if (token) {
        //TODO: Important, security: check if token is expired
        headers.set('authorization', `Bearer ${token}`)
      }
      return headers
    },
  }),
  //TODO: Figure out how to only reset for specific meetings instead of for all meetings
  tagTypes: [
    'MeetingDetails',
    'User',
    'clubAdminMembers',
    'clubAddresses',
    'clubSchedule',
    'clubDetails',
    'clubBilling',
    'clubDigest',
    'clubDigestTemplate',
  ],
  endpoints: (builder) => ({
    createGuestUser: builder.mutation<void, CreateGuestUserAppRequest>({
      query: (request) => {
        return {
          url: `/guestUser`,
          method: 'POST',
          body: request.body,
          headers: {
            Authorization: `Bearer ${request.accessToken}`,
            'Content-Type': 'application/json',
          },
        }
      },
      invalidatesTags: ['MeetingDetails'],
    }),
    generateSignInLink: builder.mutation<void, SignInLinkRequest>({
      query: (request) => {
        return {
          url: `/auth/sign-in-link`,
          method: 'POST',
          body: request,
          headers: {
            'Content-Type': 'application/json',
          },
        }
      },
    }),
    generateSignUpLink: builder.mutation<void, SignUpLinkRequest>({
      query: (request) => {
        return {
          url: `/auth/sign-up-link`,
          method: 'POST',
          body: request,
          headers: {
            'Content-Type': 'application/json',
          },
        }
      },
    }),
    acceptInvite: builder.mutation<void, AcceptInviteAppRequest>({
      query: (request) => {
        return {
          url: `/accept-invite/${request.inviteCode}`,
          method: 'POST',
          headers: {
            Authorization: `Bearer ${request.accessToken}`,
            'Content-Type': 'application/json',
          },
        }
      },
    }),
    unsubscribeEmail: builder.mutation<void, string>({
      query: (token) => ({
        url: `/email/unsubscribe/${token}`,
        method: 'POST',
      }),
    }),
    getUserDetails: builder.query<User, void>({
      query: () => `/user`,
      providesTags: ['User'],
    }),
    // Materializes/refreshes the backend user from the Firebase token claims.
    // Empty body — the auth header is injected by prepareHeaders. Must succeed
    // before POST /clubs, otherwise that call 404s on an unknown user.
    updateUser: builder.mutation<User, void>({
      query: () => ({
        url: `/user`,
        method: 'PUT',
      }),
      invalidatesTags: ['User'],
    }),
    createClub: builder.mutation<CreateClubResponse, CreateClubRequest>({
      query: (body) => ({
        url: `/clubs`,
        method: 'POST',
        body,
      }),
    }),
    getUserAchievements: builder.query<Achievements, void>({
      query: () => `/users/achievements`,
      //TODO: cache achievements separately
      providesTags: ['User'],
    }),
    getClubs: builder.query<Club[], void>({
      query: () => `/clubs`,
    }),
    // Speech pathway metadata (code/label/group) sourced from the backend enum,
    // used to build the grouped pathway dropdown and to map codes to labels for
    // display. Static reference data — no tag invalidation needed.
    getPathways: builder.query<Pathway[], void>({
      query: () => `/pathways`,
    }),
    getClubDetails: builder.query<ClubDetails, string>({
      query: (clubId) => `/club/${clubId}`,
      // Club details embed each meeting's address/logo, so address (and schedule)
      // edits must invalidate this cache to keep the public meeting cards fresh.
      providesTags: ['clubDetails'],
    }),
    getMeetingDetails: builder.query<MeetingDetails, MeetingDetailsRequest>({
      query: (request: MeetingDetailsRequest) =>
        `/club/${request.clubId}/meeting/${request.meetingTimestampUtc}`,
      providesTags: ['MeetingDetails'],
    }),
    scheduleRole: builder.mutation<
      AssignRoleResponse,
      AssignRoleRequestWithUrlParameters
    >({
      query: (request) => {
        return {
          url: `/club/${request.clubId}/meeting/${request.meetingTimestampUtc}/role`,
          method: 'POST',
          body: request.body,
        }
      },
      invalidatesTags: ['MeetingDetails'],
    }),
    updateSpeech: builder.mutation<
      void,
      {
        clubId: string
        meetingTimestampUtc: string
        body: UpdateSpeechRequest
      }
    >({
      query: (request) => ({
        url: `/club/${request.clubId}/meeting/${request.meetingTimestampUtc}/speech`,
        method: 'PATCH',
        body: request.body,
      }),
      invalidatesTags: ['MeetingDetails'],
    }),
    clubAdminUpdateMemberSpeech: builder.mutation<
      void,
      {
        clubId: string
        meetingTimestampUtc: string
        body: AdminUpdateSpeechRequest
      }
    >({
      query: (request) => ({
        url: `/club/${request.clubId}/admin/${request.meetingTimestampUtc}/speech`,
        method: 'PATCH',
        body: request.body,
      }),
      invalidatesTags: ['MeetingDetails'],
    }),
    // Word of the Day / Theme for a meeting occurrence. Both body fields are
    // optional and independent; a blank value clears that field. The
    // MeetingDetails tag drives the details query, so the page refreshes on save.
    updateMeetingAgenda: builder.mutation<
      void,
      {
        clubId: string
        meetingTimestampUtc: string
        body: UpdateMeetingAgendaRequest
      }
    >({
      query: (request) => ({
        url: `/club/${request.clubId}/meeting/${request.meetingTimestampUtc}/agenda`,
        method: 'PUT',
        body: request.body,
      }),
      invalidatesTags: ['MeetingDetails'],
    }),
    cancelAttendance: builder.mutation<
      CancelAttendanceResponse,
      CancelAttendanceRequestWithUrlParameters
    >({
      query: (request) => {
        return {
          url: `/club/${request.clubId}/meeting/${request.meetingTimestampUtc}/role`,
          method: 'DELETE',
          body: request.body,
        }
      },
      invalidatesTags: ['MeetingDetails'],
    }),
    updateUserName: builder.mutation<void, PatchUserAppRequest>({
      query: (request) => {
        return {
          url: `/user`,
          method: 'PATCH',
          body: request.body,
        }
      },
      invalidatesTags: ['User'],
    }),

    //Club Admin
    clubAdminGetClubMembers: builder.query<Array<ClubMember>, string>({
      query: (clubId) => `/club/${clubId}/admin/members`,
      providesTags: ['clubAdminMembers'],
    }),
    clubAdminAddMember: builder.mutation<
      ClubMember,
      CreateClubMemberAppRequest
    >({
      query: (request) => {
        return {
          url: `/club/${request.clubId}/admin/members`,
          method: 'POST',
          body: request.body,
        }
      },
      invalidatesTags: ['clubAdminMembers'],
    }),
    clubAdminUpdateMember: builder.mutation<void, UpdateClubMemberAppRequest>({
      query: (request) => {
        return {
          url: `/club/${request.clubId}/admin/members/${request.userId}`,
          method: 'PUT',
          body: request.body,
        }
      },
      invalidatesTags: ['clubAdminMembers'],
    }),
    clubAdminDeleteMember: builder.mutation<void, DeleteClubMemberAppRequest>({
      query: (request) => {
        return {
          url: `/club/${request.clubId}/admin/members/${request.userId}`,
          method: 'DELETE',
        }
      },
      invalidatesTags: ['clubAdminMembers'],
    }),
    clubAdminImportPreview: builder.mutation<
      ImportPreviewResponse,
      { clubId: string; file: File }
    >({
      query: ({ clubId, file }) => {
        const formData = new FormData()
        formData.append('file', file)
        return {
          url: `/club/${clubId}/admin/members/import/preview`,
          method: 'POST',
          body: formData,
          // IMPORTANT: do NOT set Content-Type — the browser must set the multipart boundary.
        }
      },
    }),
    clubAdminAddMemberBatch: builder.mutation<
      BatchAddMembersResponse,
      { clubId: string; body: BatchAddMembersRequest }
    >({
      query: ({ clubId, body }) => {
        return {
          url: `/club/${clubId}/admin/members/batch`,
          method: 'POST',
          body,
        }
      },
      invalidatesTags: ['clubAdminMembers'],
    }),
    sendInvites: builder.mutation<void, ClubAdminInviteAppRequest>({
      query: (request) => {
        return {
          url: `/club/${request.clubId}/admin/invite`,
          method: 'POST',
          body: request.body,
        }
      },
      // Refresh the member list so an individual invite shows up immediately
      // (CSV import already invalidates this via the batch endpoint).
      invalidatesTags: ['clubAdminMembers'],
    }),

    //Club Admin — Addresses
    clubAdminListAddresses: builder.query<Array<Address>, string>({
      query: (clubId) => `/club/${clubId}/admin/addresses`,
      providesTags: ['clubAddresses'],
    }),
    clubAdminCreateAddress: builder.mutation<Address, CreateAddressAppRequest>({
      query: ({ clubId, body, file }) => ({
        url: `/club/${clubId}/admin/addresses`,
        method: 'POST',
        body: buildAddressFormData(body, file),
        // IMPORTANT: do NOT set Content-Type — the browser must set the multipart boundary.
      }),
      invalidatesTags: ['clubAddresses', 'clubDetails'],
    }),
    clubAdminUpdateAddress: builder.mutation<Address, UpdateAddressAppRequest>({
      query: ({ clubId, addressId, body, file }) => ({
        url: `/club/${clubId}/admin/addresses/${addressId}`,
        method: 'PUT',
        body: buildAddressFormData(body, file),
        // IMPORTANT: do NOT set Content-Type — the browser must set the multipart boundary.
      }),
      // Updating an address changes what attached meeting cards show, so refresh
      // the club details cache too.
      invalidatesTags: ['clubAddresses', 'clubDetails'],
    }),
    clubAdminDeleteAddress: builder.mutation<void, DeleteAddressAppRequest>({
      query: ({ clubId, addressId }) => ({
        url: `/club/${clubId}/admin/addresses/${addressId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['clubAddresses', 'clubDetails'],
    }),

    //Club Admin — Schedule
    clubAdminListSchedule: builder.query<Array<RecurringMeeting>, string>({
      query: (clubId) => `/club/${clubId}/admin/schedule`,
      providesTags: ['clubSchedule'],
    }),
    clubAdminCreateScheduleEntry: builder.mutation<
      RecurringMeeting,
      CreateScheduleAppRequest
    >({
      query: ({ clubId, body }) => ({
        url: `/club/${clubId}/admin/schedule`,
        method: 'POST',
        body,
      }),
      // Creating/deleting a meeting can change which entry is isPrimary, and which
      // addresses are "in use", so refresh both caches. The public club page reads
      // the schedule from clubDetails, so invalidate that too or its meeting cards
      // go stale until a hard refresh.
      invalidatesTags: ['clubSchedule', 'clubAddresses', 'clubDetails'],
    }),
    clubAdminUpdateScheduleEntry: builder.mutation<
      RecurringMeeting,
      UpdateScheduleAppRequest
    >({
      query: ({ clubId, meetingId, body }) => ({
        url: `/club/${clubId}/admin/schedule/${meetingId}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['clubSchedule', 'clubAddresses', 'clubDetails'],
    }),
    clubAdminDeleteScheduleEntry: builder.mutation<
      void,
      DeleteScheduleAppRequest
    >({
      query: ({ clubId, meetingId }) => ({
        url: `/club/${clubId}/admin/schedule/${meetingId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['clubSchedule', 'clubAddresses', 'clubDetails'],
    }),

    //Club Admin — Billing
    clubAdminGetBillingStatus: builder.query<BillingStatusDto, string>({
      query: (clubId) => `/club/${clubId}/admin/billing`,
      providesTags: ['clubBilling'],
    }),
    clubAdminCreateCheckoutSession: builder.mutation<BillingRedirectDto, string>(
      {
        query: (clubId) => ({
          url: `/club/${clubId}/admin/billing/checkout-session`,
          method: 'POST',
        }),
      }
    ),
    clubAdminCreatePortalSession: builder.mutation<BillingRedirectDto, string>({
      query: (clubId) => ({
        url: `/club/${clubId}/admin/billing/portal-session`,
        method: 'POST',
      }),
    }),

    //Club Admin — Meeting digest (singleton per club)
    // GET 404s when the club has never configured a digest; callers treat that
    // as the "unconfigured" state and render the form with sensible defaults.
    clubAdminGetDigest: builder.query<DigestConfig, string>({
      query: (clubId) => `/club/${clubId}/admin/digest`,
      providesTags: ['clubDigest'],
    }),
    // Upsert: PUT creates the digest when none exists and edits it otherwise, so
    // there is no separate create path.
    clubAdminUpsertDigest: builder.mutation<
      DigestConfig,
      { clubId: string; body: DigestConfigUpsertRequest }
    >({
      query: ({ clubId, body }) => ({
        url: `/club/${clubId}/admin/digest`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['clubDigest'],
    }),
    clubAdminDeleteDigest: builder.mutation<void, string>({
      query: (clubId) => ({
        url: `/club/${clubId}/admin/digest`,
        method: 'DELETE',
      }),
      invalidatesTags: ['clubDigest'],
    }),
    clubAdminSendDigestNow: builder.mutation<DigestSendNowResult, string>({
      query: (clubId) => ({
        url: `/club/${clubId}/admin/digest/send`,
        method: 'POST',
      }),
    }),

    //Club Admin — Custom email template (lives on the governing digest config)
    // GET never 404s: an unconfigured club gets { customTemplateOn: false, json: null }.
    clubAdminGetTemplate: builder.query<EmailTemplateResponse, string>({
      query: (clubId) => `/club/${clubId}/admin/digest/template`,
      providesTags: ['clubDigestTemplate'],
    }),
    clubAdminSaveTemplate: builder.mutation<
      EmailTemplateResponse,
      { clubId: string; body: EmailTemplateUpsertRequest }
    >({
      query: ({ clubId, body }) => ({
        url: `/club/${clubId}/admin/digest/template`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['clubDigestTemplate'],
    }),
    // Flip customTemplateOn without re-saving the draft. Enabling with nothing
    // stored 404s, so callers only allow it once a template has been saved.
    clubAdminSetTemplateEnabled: builder.mutation<
      EmailTemplateResponse,
      { clubId: string; on: boolean }
    >({
      query: ({ clubId, on }) => ({
        url: `/club/${clubId}/admin/digest/template/enabled`,
        method: 'PUT',
        body: { on },
      }),
      invalidatesTags: ['clubDigestTemplate'],
    }),
    clubAdminClearTemplate: builder.mutation<void, string>({
      query: (clubId) => ({
        url: `/club/${clubId}/admin/digest/template`,
        method: 'DELETE',
      }),
      invalidatesTags: ['clubDigestTemplate'],
    }),
    clubAdminUploadTemplateImage: builder.mutation<
      { url: string },
      { clubId: string; file: File }
    >({
      query: ({ clubId, file }) => {
        const formData = new FormData()
        formData.append('file', file)
        return {
          url: `/club/${clubId}/admin/digest/template/image`,
          method: 'POST',
          body: formData,
          // IMPORTANT: do NOT set Content-Type — the browser must set the multipart boundary.
        }
      },
    }),

    //Meeting Admin
    clubAdminAssignMemberRole: builder.mutation<
      AssignRoleResponse,
      AssignMemberRoleForMeetingAdminAppRequest
    >({
      query: (request) => {
        return {
          url: `/club/${request.clubId}/admin/${request.meetingTimestampUtc}/role`,
          method: 'PUT',
          body: request.body,
        }
      },
      invalidatesTags: ['MeetingDetails'],
    }),
    clubAdminCancelMemberRole: builder.mutation<
      CancelAttendanceResponse,
      CancelMemberRoleForMeetingAdminAppRequest
    >({
      query: (request) => {
        return {
          url: `/club/${request.clubId}/admin/${request.meetingTimestampUtc}/role`,
          method: 'DELETE',
          body: request.body,
        }
      },
      invalidatesTags: ['MeetingDetails'],
    }),
    clubAdminBatchAssignRoles: builder.mutation<
      BatchRolesResponse,
      BatchAssignRolesAppRequest
    >({
      query: (request) => {
        return {
          url: `/club/${request.clubId}/admin/${request.meetingTimestampUtc}/roles/batch-assign`,
          method: 'POST',
          body: request.body,
        }
      },
      invalidatesTags: ['MeetingDetails'],
    }),
    clubAdminBatchCancelRoles: builder.mutation<
      BatchRolesResponse,
      BatchCancelRolesAppRequest
    >({
      query: (request) => {
        return {
          url: `/club/${request.clubId}/admin/${request.meetingTimestampUtc}/roles/batch-cancel`,
          method: 'POST',
          body: request.body,
        }
      },
      invalidatesTags: ['MeetingDetails'],
    }),
  }),
})

export const {
  useCreateGuestUserMutation,
  useAcceptInviteMutation,
  useUnsubscribeEmailMutation,
  useGenerateSignInLinkMutation,
  useGenerateSignUpLinkMutation,
  useGetUserDetailsQuery,
  useUpdateUserMutation,
  useCreateClubMutation,
  useGetUserAchievementsQuery,
  useGetClubsQuery,
  useGetPathwaysQuery,
  useGetClubDetailsQuery,
  useGetMeetingDetailsQuery,
  useScheduleRoleMutation,
  useUpdateSpeechMutation,
  useClubAdminUpdateMemberSpeechMutation,
  useUpdateMeetingAgendaMutation,
  useCancelAttendanceMutation,
  useUpdateUserNameMutation,
  useClubAdminGetClubMembersQuery,
  useClubAdminUpdateMemberMutation,
  useClubAdminDeleteMemberMutation,
  useSendInvitesMutation,
  useClubAdminImportPreviewMutation,
  useClubAdminAddMemberBatchMutation,
  useClubAdminAssignMemberRoleMutation,
  useClubAdminCancelMemberRoleMutation,
  useClubAdminBatchAssignRolesMutation,
  useClubAdminBatchCancelRolesMutation,
  useClubAdminListAddressesQuery,
  useClubAdminCreateAddressMutation,
  useClubAdminUpdateAddressMutation,
  useClubAdminDeleteAddressMutation,
  useClubAdminGetBillingStatusQuery,
  useLazyClubAdminGetBillingStatusQuery,
  useClubAdminCreateCheckoutSessionMutation,
  useClubAdminCreatePortalSessionMutation,
  useClubAdminGetDigestQuery,
  useClubAdminUpsertDigestMutation,
  useClubAdminDeleteDigestMutation,
  useClubAdminSendDigestNowMutation,
  useClubAdminGetTemplateQuery,
  useClubAdminSaveTemplateMutation,
  useClubAdminSetTemplateEnabledMutation,
  useClubAdminClearTemplateMutation,
  useClubAdminUploadTemplateImageMutation,
  useClubAdminListScheduleQuery,
  useClubAdminCreateScheduleEntryMutation,
  useClubAdminUpdateScheduleEntryMutation,
  useClubAdminDeleteScheduleEntryMutation,
} = speechAgendaApi
